import { settings } from '../constants';
import { toRomaji } from 'wanakana';
import ReactDOMServer from 'react-dom/server';
import Swal from 'sweetalert2';
import { generatePDF } from '../utils/pdfUtils';
import { errorMessages } from './messagesUtils';
import WebCvPDF from '../pages/public/WebCv/WebCvPDF/WebCvPDF';
import dayjs from 'dayjs';

// 性別を表示する関数
export const formatGender = (gender) => {
    if (gender === settings.GENDER.NOT_SELECTED) {
        return '無回答';
    } else if (gender === settings.GENDER.MALE) {
        return '男性';
    } else if (gender === settings.GENDER.FEMALE) {
        return '女性';
    }
    return '未登録';
};

// 学歴の種類を表示する関数
export const formatEducationStatus = (no) => {
    let type = parseInt(no);
    if (type === 1) {
        return '卒業';
    } else if (type === 2) {
        return '中退';
    } else if (type === 3) {
        return '在学中';
    }
}

// 誕生日から年齢を計算する関数
export const calculateAge = (birth_year, birth_month, birth_day) => {
    if (!birth_year || !birth_month || !birth_day) {
        return '未登録';
    }
    const today = new Date();
    const birthDate = new Date(birth_year, birth_month - 1, birth_day);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return `${age}歳`;
}

// 開始年月と終了年月から期間を計算する関数
export const calculatePeriod = (startYear, startMonth, endYear, endMonth) => {
        // 合計月数の計算
        if (endYear === '' && endMonth === '') {
            const currentDate = new Date();
            endYear = currentDate.getFullYear();
            endMonth = currentDate.getMonth() + 1; // JavaScriptの月は0から始まるため+1
        }
        const totalMonths = (parseInt(endYear) - parseInt(startYear)) * 12 + (parseInt(endMonth) - parseInt(startMonth)) + 1;
        
        // 合計月数を年月に変換
        const years = Math.floor(totalMonths / 12);
        const months = totalMonths % 12;
        
        let label = '';
        
        if (years >= 1) {
            // 1年以上の場合
            if (months > 0) {
                label = `${years}年${months}か月`;
            } else {
                label = `${years}年`;
            }
        } else if (months > 0) {
            // 1年未満の場合
            label = `${months}か月`;
        } else {
            label = '1か月未満';
        }
        return label;
    }

// イニシャル表示へ変換する(出力PDFファイル名用で姓と名の間にドットがない)
const generateInitialsForFileName  = (lastNameKana, firstNameKana) => {
    if (!lastNameKana || !firstNameKana) {
        return '';
    }
    const lastNameRomaji = toRomaji(lastNameKana);
    const lastNameInitial = lastNameRomaji.charAt(0).toUpperCase();
    const firstNameRomaji = toRomaji(firstNameKana);
    const firstNameInitial = firstNameRomaji.charAt(0).toUpperCase();
    return `${firstNameInitial}${lastNameInitial}`;
}

// 姓をイニシャル表示へ変換する
export const generateLastNameInitial = (lastNameKana) => {
    if (!lastNameKana) {
        return '';
    }
    const lastNameRomaji = toRomaji(lastNameKana);
    const lastNameInitial = lastNameRomaji.charAt(0).toUpperCase();
    return lastNameInitial;
}

// 名をイニシャル表示へ変換する
export const generateFirstNameInitial = (firstNameKana) => {
    if (!firstNameKana) {
        return '';
    }
    const firstNameRomaji = toRomaji(firstNameKana);
    const firstNameInitial = firstNameRomaji.charAt(0).toUpperCase();
    return firstNameInitial;
}

// 適切な日付表示へ変換する(出力PDFファイル内用で姓と名の間にドットがある)
const generateDateForFileName = () => {
    const now = new Date();
    // YY年MM月DD日 の形式に変換
    const year = now.getFullYear().toString().slice(-2); // 下2桁を取得
    // 月と日は必ず2桁になるようにフォーマット
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    // YYMMDDの形式で返す
    return `${year}${month}${day}`;
};

// PDFエクスポート関数
export const handleExportPDF = async (data) => {
    // プロフィール・プロジェクトのいずれかが欠けていたら出力不可
    if (!data?.engineer || !data?.projects || !data?.profile) {
        await Swal.fire({ icon: 'error',
            html: errorMessages.webcv.not_enough_data,
            customClass: {
                htmlContainer: 'large-text'
            }
        });
        return;
    }
    try {
        // PDF出力のため、コンポネントを文字列に変換
        const htmlString = ReactDOMServer.renderToString(<WebCvPDF data={data} />);
        // 文字列をDOM要素に変換
        const element = document.createElement('div');
        element.innerHTML = htmlString;
        document.body.appendChild(element);

        // 動的に生成した日付でファイル名を設定
        const dateForFileName = generateDateForFileName();

        // PDF出力
        await generatePDF(
            element,
            `スキルシート_${generateInitialsForFileName (data?.engineer?.last_name_kana, data?.engineer?.first_name_kana) || ''}_${dateForFileName}`
        );

        // 一時要素を削除
        document.body.removeChild(element);
    } catch (error) {
        await Swal.fire({ icon: 'error',
            html: errorMessages.webcv.export_failed,
            customClass: {
                htmlContainer: 'large-text'
            }
        });
    }
}

// サーバー、クライアント、開発、クラウド、作業PC、ネットワークのテーブルを作成する
export const groupItemsByType = (items, typeKey, conKey) => {
    if (!items || !Array.isArray(items)) return {};
    
    const itemMap = new Map();

    items.forEach(item => {
    const type = item[typeKey][0].value;
    const con = item[conKey][0].value;

    // valueが空文字列の場合はスキップ
    if (type === '' || con === '') return;

    if (!itemMap.has(type)) {
        itemMap.set(type, []);
    }

    itemMap.get(type).push(con);
    });

    return itemMap.size > 0 ? Object.fromEntries(itemMap) : {};
};

// 全ての日付を比較して最新の更新日を返す
export const getLatestUpdateDate = (engineer) => {
    const dates = [
        engineer?.profile?.updatedAt,
        engineer?.certifications?.updatedAt,
        engineer?.projects?.updatedAt,
        engineer?.works?.updatedAt
    ].filter(date => date); // null や undefined を除外

    if (dates.length === 0) return null;

    return dates.reduce((latest, current) => {
        return dayjs(current).isAfter(dayjs(latest)) ? current : latest;
    });
};